/**
 * Track A — core geometry. See docs/SPEC.md and docs/recon/geometry-web.json.
 * Pure functions over typed arrays; no DOM, no deps (must run in workers and vitest/node).
 */
import type { BBox, Mat, Pt, Ring } from "./types";

/** Geometry epsilon (matches the Python engine's EPS for close_poly / bbox padding). */
const EPS = 1e-7;

/** %.6f, trailing zeros stripped; |n| < 5e-7 and "-0" -> "0". */
export function fmt(n: number): string {
  if (!Number.isFinite(n)) return "0";
  let v = n;
  if (Math.abs(v) < 0.0000005) v = 0;
  let text = v.toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
  if (text === "" || text === "-" || text === "-0") return "0";
  return text;
}

export function matMul(parent: Mat, child: Mat): Mat {
  const [a1, b1, c1, d1, e1, f1] = parent;
  const [a2, b2, c2, d2, e2, f2] = child;
  return [
    a1 * a2 + c1 * b2,
    b1 * a2 + d1 * b2,
    a1 * c2 + c1 * d2,
    b1 * c2 + d1 * d2,
    a1 * e2 + c1 * f2 + e1,
    b1 * e2 + d1 * f2 + f1,
  ];
}

export function applyMat(m: Mat, x: number, y: number): Pt {
  return { x: m[0] * x + m[2] * y + m[4], y: m[1] * x + m[3] * y + m[5] };
}

/** Parse "a b c d e f" (whitespace/comma separated); invalid -> identity. */
export function parseMatText(text: string | null | undefined): Mat {
  if (!text) return [1, 0, 0, 1, 0, 0];
  const parts = text.trim().split(/[\s,]+/).filter((s) => s.length > 0);
  if (parts.length < 6) return [1, 0, 0, 1, 0, 0];
  const nums = parts.slice(0, 6).map((s) => Number(s));
  for (const v of nums) {
    if (!Number.isFinite(v)) return [1, 0, 0, 1, 0, 0];
  }
  return [nums[0], nums[1], nums[2], nums[3], nums[4], nums[5]];
}

export function bboxOfFlat(points: Float64Array): BBox {
  const n = points.length;
  if (n < 2) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < n; i += 2) {
    const x = points[i];
    const y = points[i + 1];
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}

/** Shoelace signed area of an implicitly-closed flat ring. Callers only compare signs/magnitudes. */
export function signedArea(ring: Ring): number {
  const n = ring.length;
  if (n < 6) return 0;
  let sum = 0;
  let px = ring[n - 2];
  let py = ring[n - 1];
  for (let i = 0; i + 1 < n; i += 2) {
    const x = ring[i];
    const y = ring[i + 1];
    sum += px * y - x * py;
    px = x;
    py = y;
  }
  return sum * 0.5;
}

/**
 * Adaptive cubic bezier flattening (recursive de Casteljau with a chord-flatness
 * test — the dependency-free variant endorsed by docs/recon/geometry-web.json).
 * tol in mm; a control point within `tol` of the chord bounds the true curve
 * deviation to <= 0.75·tol. curveSteps caps subdivision depth (quality clamp).
 * Appends flattened points (excluding p0, including p1) to out.
 */
export function flattenCubic(
  out: number[],
  p0x: number, p0y: number, c0x: number, c0y: number,
  c1x: number, c1y: number, p1x: number, p1y: number,
  tol: number, maxSteps: number,
): void {
  const t = Math.abs(tol) > 1e-9 ? Math.abs(tol) : 1e-9;
  const cap = Math.max(1, Math.min(96, Math.floor(maxSteps) || 1));
  const maxDepth = Math.min(14, Math.max(1, Math.ceil(Math.log2(cap + 1))));

  const rec = (
    ax: number, ay: number, b0x: number, b0y: number,
    b1x: number, b1y: number, dx: number, dy: number, depth: number,
  ): void => {
    const ux = dx - ax;
    const uy = dy - ay;
    const len2 = ux * ux + uy * uy;
    let d0: number;
    let d1: number;
    if (len2 < 1e-24) {
      d0 = Math.hypot(b0x - ax, b0y - ay);
      d1 = Math.hypot(b1x - ax, b1y - ay);
    } else {
      const inv = 1 / Math.sqrt(len2);
      d0 = Math.abs((b0x - ax) * uy - (b0y - ay) * ux) * inv;
      d1 = Math.abs((b1x - ax) * uy - (b1y - ay) * ux) * inv;
    }
    if (depth >= maxDepth || (d0 <= t && d1 <= t)) {
      out.push(dx, dy);
      return;
    }
    const ab0x = (ax + b0x) * 0.5, ab0y = (ay + b0y) * 0.5;
    const b01x = (b0x + b1x) * 0.5, b01y = (b0y + b1y) * 0.5;
    const b1dx = (b1x + dx) * 0.5, b1dy = (b1y + dy) * 0.5;
    const abb0x = (ab0x + b01x) * 0.5, abb0y = (ab0y + b01y) * 0.5;
    const bb1x = (b01x + b1dx) * 0.5, bb1y = (b01y + b1dy) * 0.5;
    const mx = (abb0x + bb1x) * 0.5, my = (abb0y + bb1y) * 0.5;
    rec(ax, ay, ab0x, ab0y, abb0x, abb0y, mx, my, depth + 1);
    rec(mx, my, bb1x, bb1y, b1dx, b1dy, dx, dy, depth + 1);
  };

  rec(p0x, p0y, c0x, c0y, c1x, c1y, p1x, p1y, 0);
}

/** Rounded/plain rect outline as flat ring, centered at origin (LightBurn Rect is center-origin). */
export function rectRing(w: number, h: number, cornerRadius: number, segsPerCorner: number): Ring {
  const w2 = w / 2;
  const h2 = h / 2;
  let cr = Math.max(0, Math.min(cornerRadius, w2, h2));
  if (!(cr > EPS)) {
    return Float64Array.of(-w2, -h2, w2, -h2, w2, h2, -w2, h2);
  }
  const seg = Math.max(1, Math.floor(segsPerCorner));
  const corners: Array<[number, number, number, number]> = [
    [w2 - cr, -h2 + cr, -Math.PI / 2, 0],
    [w2 - cr, h2 - cr, 0, Math.PI / 2],
    [-w2 + cr, h2 - cr, Math.PI / 2, Math.PI],
    [-w2 + cr, -h2 + cr, Math.PI, (3 * Math.PI) / 2],
  ];
  const pts: number[] = [];
  for (const [cx, cy, a0, a1] of corners) {
    for (let i = 0; i <= seg; i++) {
      const a = a0 + ((a1 - a0) * i) / seg;
      pts.push(cx + Math.cos(a) * cr, cy + Math.sin(a) * cr);
    }
  }
  return Float64Array.from(pts);
}

export function ellipseRing(rx: number, ry: number, segments: number): Ring {
  const seg = Math.max(16, Math.min(256, Math.floor(segments)));
  const pts = new Float64Array(seg * 2);
  for (let i = 0; i < seg; i++) {
    const a = (2 * Math.PI * i) / seg;
    pts[i * 2] = Math.cos(a) * rx;
    pts[i * 2 + 1] = Math.sin(a) * ry;
  }
  return pts;
}

/** Regular polygon, first vertex at angle rot (radians); LightBurn default rot = -PI/2. */
export function regularPolygonRing(rx: number, ry: number, sides: number, rot: number): Ring {
  const n = Math.max(3, Math.min(64, Math.floor(sides)));
  const pts = new Float64Array(n * 2);
  for (let i = 0; i < n; i++) {
    const a = rot + (2 * Math.PI * i) / n;
    pts[i * 2] = Math.cos(a) * rx;
    pts[i * 2 + 1] = Math.sin(a) * ry;
  }
  return pts;
}

/** N-pointed star: alternating outer/inner vertices, first point at angle rot. */
export function starRing(rOuter: number, rInner: number, points: number, rot: number): Ring {
  const n = Math.max(3, Math.min(24, Math.floor(points)));
  const pts = new Float64Array(n * 4);
  for (let i = 0; i < n * 2; i++) {
    const a = rot + (Math.PI * i) / n;
    const rr = i % 2 === 0 ? rOuter : rInner;
    pts[i * 2] = Math.cos(a) * rr;
    pts[i * 2 + 1] = Math.sin(a) * rr;
  }
  return pts;
}

/**
 * Heart outline centered at the origin, fit uniformly inside the [-r, r] box.
 * Traced from the classic sin^3 / cos parametric curve. Screen coords are y-down,
 * so the point sits at the BOTTOM (a normal upright heart); callers flip Y for
 * staggered rows. `segments` sets smoothness.
 */
export function heartRing(r: number, segments: number): Ring {
  const n = Math.max(16, Math.min(128, Math.floor(segments)));
  const xs = new Float64Array(n);
  const ys = new Float64Array(n);
  let ext = 0;
  for (let i = 0; i < n; i++) {
    const t = (2 * Math.PI * i) / n;
    const x = 16 * Math.pow(Math.sin(t), 3);
    // y-down screen space: negate the math-up curve so the cusp is on top and
    // the tip points down.
    const y = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
    xs[i] = x;
    ys[i] = y;
    if (Math.abs(x) > ext) ext = Math.abs(x);
    if (Math.abs(y) > ext) ext = Math.abs(y);
  }
  const s = ext > 0 ? r / ext : 1;
  const pts = new Float64Array(n * 2);
  for (let i = 0; i < n; i++) {
    pts[i * 2] = xs[i] * s;
    pts[i * 2 + 1] = ys[i] * s;
  }
  return pts;
}

export interface PixelShapeOpts {
  /** Vertical stretch (ry ÷ rx). 1 = as authored. */
  aspect?: number;
  /** Mirror the shape vertically (staggered-row hearts). */
  flipY?: boolean;
}

/**
 * Pixel shape outline(s) centered at (cx, cy). Cross returns TWO rectangle
 * rings (its convex parts); all other shapes return one convex ring.
 * Circle is approximated with max(12, min(32, ceil(size/0.02))) segments.
 * `opts.aspect` stretches height; `opts.flipY` mirrors vertically.
 */
export function pixelShapeRings(shape: string, cx: number, cy: number, size: number, opts?: PixelShapeOpts): Ring[] {
  const r = size / 2;
  // Build each shape centered at the origin, then apply aspect/flip/translate
  // uniformly so every shape honours the stretch + mirror options.
  let local: Ring[];
  switch (shape) {
    case "square":
      local = [Float64Array.of(-r, -r, r, -r, r, r, -r, r)];
      break;
    case "diamond":
      // A diamond is a square turned 45deg: a 4-gon with vertices on the axes
      // (up/down/left/right). rot=0 puts the first vertex at (r,0); rot=PI/4
      // would instead land the vertices on the corners and render an axis-aligned
      // square, which is the separate "square" shape.
      local = [regularPolygonRing(r, r, 4, 0)];
      break;
    case "hexagon":
      local = [regularPolygonRing(r, r, 6, 0)];
      break;
    case "octagon":
      // rot = PI/8 gives a flat top and bottom edge (the familiar stop-sign look).
      local = [regularPolygonRing(r, r, 8, Math.PI / 8)];
      break;
    case "triangle":
      local = [regularPolygonRing(r, r, 3, -Math.PI / 2)];
      break;
    case "star":
      // 5-pointed star, point up. Inner radius 0.5*outer is the classic look.
      local = [starRing(r, r * 0.5, 5, -Math.PI / 2)];
      break;
    case "heart":
      local = [heartRing(r, Math.max(24, Math.ceil(size / 0.01)))];
      break;
    case "cross": {
      const bar = Math.max(size * 0.33, size / 6);
      const hb = bar / 2;
      local = [
        Float64Array.of(-r, -hb, r, -hb, r, hb, -r, hb),
        Float64Array.of(-hb, -r, hb, -r, hb, r, -hb, r),
      ];
      break;
    }
    case "circle":
    default: {
      const seg = Math.max(12, Math.min(32, Math.ceil(size / 0.02)));
      const pts = new Float64Array(seg * 2);
      for (let i = 0; i < seg; i++) {
        const a = (2 * Math.PI * i) / seg;
        pts[i * 2] = Math.cos(a) * r;
        pts[i * 2 + 1] = Math.sin(a) * r;
      }
      local = [pts];
      break;
    }
  }

  const aspect = opts?.aspect !== undefined && opts.aspect > 0 ? opts.aspect : 1;
  const sy = (opts?.flipY ? -1 : 1) * aspect;
  if (aspect === 1 && sy === 1) return local.map((ring) => translateRing(ring, cx, cy));
  return local.map((ring) => {
    const out = new Float64Array(ring.length);
    for (let i = 0; i + 1 < ring.length; i += 2) {
      out[i] = ring[i] + cx;
      out[i + 1] = ring[i + 1] * sy + cy;
    }
    return out;
  });
}

function translateRing(ring: Ring, dx: number, dy: number): Ring {
  const out = new Float64Array(ring.length);
  for (let i = 0; i + 1 < ring.length; i += 2) {
    out[i] = ring[i] + dx;
    out[i + 1] = ring[i + 1] + dy;
  }
  return out;
}

/** Even-odd crossing test of a point against ONE implicitly-closed flat ring (matches Python point_in_polygon). */
export function pointInFlatRing(x: number, y: number, ring: Ring): boolean {
  const n = ring.length;
  if (n < 6) return false;
  let inside = false;
  let jx = ring[n - 2];
  let jy = ring[n - 1];
  for (let i = 0; i + 1 < n; i += 2) {
    const ix = ring[i];
    const iy = ring[i + 1];
    if ((iy > y) !== (jy > y)) {
      const denom = (jy - iy) || 1e-18;
      const xAtY = ((jx - ix) * (y - iy)) / denom + ix;
      if (x < xAtY) inside = !inside;
    }
    jx = ix;
    jy = iy;
  }
  return inside;
}

/** Even-odd point-in-rings test with per-ring bbox precheck (ad-hoc queries only — grid work uses RowClassifier). */
export function pointInRingsEvenOdd(x: number, y: number, rings: readonly Ring[], bboxes: readonly BBox[]): boolean {
  let inside = false;
  for (let k = 0; k < rings.length; k++) {
    const bb = bboxes[k];
    if (bb) {
      if (x < bb.minX - EPS || x > bb.maxX + EPS || y < bb.minY - EPS || y > bb.maxY + EPS) continue;
    }
    if (pointInFlatRing(x, y, rings[k])) inside = !inside;
  }
  return inside;
}

/**
 * Scanline row classifier over a set of implicitly-closed rings (even-odd).
 * Build once per layer; crossingsAt(y) returns sorted crossing xs for that row,
 * so callers walk a whole grid row in O(crossings + cells). The strict half-open
 * `(y0>y) !== (y1>y)` rule (identical to Python point_in_polygon) inherently
 * excludes horizontal edges and resolves vertex collisions consistently, so the
 * combined crossing list is parity-identical to pointInRingsEvenOdd.
 */
export class RowClassifier {
  private ex0: Float64Array;
  private ey0: Float64Array;
  private ex1: Float64Array;
  private ey1: Float64Array;
  private count: number;

  constructor(rings: readonly Ring[]) {
    let total = 0;
    for (const ring of rings) {
      if (ring.length >= 6) total += ring.length / 2;
    }
    this.ex0 = new Float64Array(total);
    this.ey0 = new Float64Array(total);
    this.ex1 = new Float64Array(total);
    this.ey1 = new Float64Array(total);
    let e = 0;
    for (const ring of rings) {
      const n = ring.length;
      if (n < 6) continue;
      const vcount = n / 2;
      for (let i = 0; i < vcount; i++) {
        const j = (i + 1) % vcount;
        this.ex0[e] = ring[i * 2];
        this.ey0[e] = ring[i * 2 + 1];
        this.ex1[e] = ring[j * 2];
        this.ey1[e] = ring[j * 2 + 1];
        e++;
      }
    }
    this.count = e;
  }

  /** Sorted x crossings of the region boundary with the horizontal line at y (even-odd parity flips at each). */
  crossingsAt(y: number): number[] {
    const xs: number[] = [];
    const { ex0, ey0, ex1, ey1, count } = this;
    for (let i = 0; i < count; i++) {
      const y0 = ey0[i];
      const y1 = ey1[i];
      if ((y0 > y) !== (y1 > y)) {
        const denom = (y1 - y0) || 1e-18;
        xs.push(ex0[i] + ((ex1[i] - ex0[i]) * (y - y0)) / denom);
      }
    }
    xs.sort((a, b) => a - b);
    return xs;
  }
}

/**
 * Half-width of the signed cell-index window that packs into one collision-free
 * number key. Indices in [-CELL_KEY_BOUND, CELL_KEY_BOUND) pack losslessly; any
 * index outside that window (extreme coordinates / tiny cell sizes) falls back
 * to a namespaced string key that can never alias a packed number.
 */
const CELL_KEY_BOUND = 0x100000; // 2^20

/**
 * Pack a signed cell index pair into a collision-free spatial-hash key. Normal
 * coordinates take the fast numeric path (two shifted 21-bit lanes in one
 * double, exactly representable). Out-of-window indices take a string fallback
 * ("cx:cy") so an extreme coordinate can never collide with a packed pair and
 * corrupt a bucket. Shared by SegmentIndex and the pixelate RingBuckets mirror.
 */
export function packCellKey(cx: number, cy: number): number | string {
  if (
    cx >= -CELL_KEY_BOUND && cx < CELL_KEY_BOUND &&
    cy >= -CELL_KEY_BOUND && cy < CELL_KEY_BOUND
  ) {
    // Lane width 0x1000000 (2^24) > 2·CELL_KEY_BOUND, so lanes never overlap;
    // max packed value < 2^45, well within exact double-integer range.
    return (cx + CELL_KEY_BOUND) * 0x1000000 + (cy + CELL_KEY_BOUND);
  }
  return cx + ":" + cy;
}

/**
 * Spatial hash of ring segments for boundary detection. anySegmentNear returns
 * true if any segment lies within chebyshevRadius of (x, y) — i.e. intersects the
 * axis-aligned square [x-r, x+r] × [y-r, y+r].
 */
export class SegmentIndex {
  private cellSize: number;
  private buckets: Map<number | string, number[]>;
  private sx0: Float64Array;
  private sy0: Float64Array;
  private sx1: Float64Array;
  private sy1: Float64Array;
  private empty: boolean;

  constructor(rings: readonly Ring[], cellSize: number) {
    this.cellSize = cellSize > 1e-9 ? cellSize : 1e-9;
    this.buckets = new Map();
    let total = 0;
    for (const ring of rings) {
      if (ring.length >= 6) total += ring.length / 2;
      else if (ring.length >= 4) total += ring.length / 2 - 1;
    }
    this.sx0 = new Float64Array(total);
    this.sy0 = new Float64Array(total);
    this.sx1 = new Float64Array(total);
    this.sy1 = new Float64Array(total);
    let s = 0;
    for (const ring of rings) {
      const n = ring.length;
      if (n < 4) continue;
      const vcount = n / 2;
      const closed = n >= 6;
      const edges = closed ? vcount : vcount - 1;
      for (let i = 0; i < edges; i++) {
        const j = closed ? (i + 1) % vcount : i + 1;
        this.sx0[s] = ring[i * 2];
        this.sy0[s] = ring[i * 2 + 1];
        this.sx1[s] = ring[j * 2];
        this.sy1[s] = ring[j * 2 + 1];
        s++;
      }
    }
    this.empty = s === 0;
    for (let i = 0; i < s; i++) {
      this.insertSegment(i);
    }
  }

  private insertSegment(i: number): void {
    const inv = 1 / this.cellSize;
    let minx = this.sx0[i];
    let maxx = this.sx1[i];
    if (minx > maxx) { const t = minx; minx = maxx; maxx = t; }
    let miny = this.sy0[i];
    let maxy = this.sy1[i];
    if (miny > maxy) { const t = miny; miny = maxy; maxy = t; }
    const cx0 = Math.floor(minx * inv);
    const cx1 = Math.floor(maxx * inv);
    const cy0 = Math.floor(miny * inv);
    const cy1 = Math.floor(maxy * inv);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cy = cy0; cy <= cy1; cy++) {
        const key = packCellKey(cx, cy);
        let arr = this.buckets.get(key);
        if (!arr) { arr = []; this.buckets.set(key, arr); }
        arr.push(i);
      }
    }
  }

  anySegmentNear(x: number, y: number, chebyshevRadius: number): boolean {
    if (this.empty) return false;
    const r = Math.max(0, chebyshevRadius);
    const inv = 1 / this.cellSize;
    const cx0 = Math.floor((x - r) * inv);
    const cx1 = Math.floor((x + r) * inv);
    const cy0 = Math.floor((y - r) * inv);
    const cy1 = Math.floor((y + r) * inv);
    const rMinX = x - r, rMaxX = x + r, rMinY = y - r, rMaxY = y + r;
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cy = cy0; cy <= cy1; cy++) {
        const arr = this.buckets.get(packCellKey(cx, cy));
        if (!arr) continue;
        for (let k = 0; k < arr.length; k++) {
          const i = arr[k];
          if (segmentIntersectsRect(this.sx0[i], this.sy0[i], this.sx1[i], this.sy1[i], rMinX, rMinY, rMaxX, rMaxY)) {
            return true;
          }
        }
      }
    }
    return false;
  }
}

/** Liang–Barsky: does segment (x0,y0)-(x1,y1) intersect the axis-aligned rect? */
function segmentIntersectsRect(
  x0: number, y0: number, x1: number, y1: number,
  minX: number, minY: number, maxX: number, maxY: number,
): boolean {
  // Endpoints inside.
  if (x0 >= minX && x0 <= maxX && y0 >= minY && y0 <= maxY) return true;
  if (x1 >= minX && x1 <= maxX && y1 >= minY && y1 <= maxY) return true;
  let t0 = 0;
  let t1 = 1;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const p = [-dx, dx, -dy, dy];
  const q = [x0 - minX, maxX - x0, y0 - minY, maxY - y0];
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) {
      if (q[i] < 0) return false; // parallel and outside this boundary
    } else {
      const rr = q[i] / p[i];
      if (p[i] < 0) {
        if (rr > t1) return false;
        if (rr > t0) t0 = rr;
      } else {
        if (rr < t0) return false;
        if (rr < t1) t1 = rr;
      }
    }
  }
  return t0 <= t1;
}

/** Sample points along an open/closed polyline at fixed spacing (with start offset), like the Python sample_polyline. */
export function samplePolyline(points: Float64Array, closed: boolean, spacing: number, offset: number): Pt[] {
  const sp = Math.max(spacing, 0.0001);
  const off = Math.max(0, offset);
  const vcount = points.length / 2;
  if (vcount < 2) return [];

  // Build working point list; closed rings append the first point (close_poly).
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < vcount; i++) {
    xs.push(points[i * 2]);
    ys.push(points[i * 2 + 1]);
  }
  if (closed) {
    const dx = xs[0] - xs[xs.length - 1];
    const dy = ys[0] - ys[ys.length - 1];
    if (Math.hypot(dx, dy) > EPS) {
      xs.push(xs[0]);
      ys.push(ys[0]);
    }
  }

  const out: Pt[] = [];
  let carry = 0;
  let nextAt = off;
  for (let idx = 0; idx + 1 < xs.length; idx++) {
    const ax = xs[idx], ay = ys[idx];
    const bx = xs[idx + 1], by = ys[idx + 1];
    const segLen = Math.hypot(bx - ax, by - ay);
    if (segLen <= EPS) continue;
    const ux = (bx - ax) / segLen;
    const uy = (by - ay) / segLen;
    let s = nextAt - carry;
    if (s < -EPS) s = 0;
    while (s <= segLen + EPS) {
      out.push({ x: ax + ux * s, y: ay + uy * s });
      s += sp;
    }
    carry += segLen;
    nextAt = Math.ceil((carry - off) / sp) * sp + off;
  }
  return out;
}
